import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  ShowChart as ShowChartIcon,
  AccountBalanceWallet as AccountBalanceWalletIcon,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useApp } from '../context/AppContext';
import { useThemeColors } from '../hooks/useThemeColors';
import Sidebar from '../components/Configuracao/Sidebar';

const meses = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

const mesesAbrev = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

// Colors for pie chart
const COLORS = [
  '#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd',
  '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf',
  '#aec7e8', '#ffbb78',
];

function Home() {
  const navigate = useNavigate();
  const { corretores, calcularVGVTotal, calcularVGVMes, getMetaPorAno } = useApp();
  const colors = useThemeColors();
  const [menuSelecionado] = useState('Início');
  
  // Always use current year as default
  const anoAtual = new Date().getFullYear();
  const anoAnterior = anoAtual - 1;
  const [anoSelecionado, setAnoSelecionado] = useState(anoAtual);
  const [anosSelecionados, setAnosSelecionados] = useState([anoAnterior, anoAtual]);

  // Helper function to get venda value (backward compatible)
  const getVendaValor = (corretor, ano, mes) => {
    const venda = corretor.vendas?.[ano]?.[mes];
    if (!venda) return 0;
    return typeof venda === 'object' ? (venda.valor || 0) : venda;
  };

  // Helper function to get agenciamento value
  const getAgenciamento = (corretor, ano, mes) => {
    const venda = corretor.vendas?.[ano]?.[mes];
    if (!venda) return 0;
    const agenciamento = typeof venda === 'object' ? (venda.agenciamento || 0) : 0;
    return parseInt(agenciamento, 10);
  };

  // Helper function to get numero_contratos value
  const getNumeroContratos = (corretor, ano, mes) => {
    const venda = corretor.vendas?.[ano]?.[mes];
    if (!venda) return 0;
    const numeroContratos = typeof venda === 'object' ? (venda.numero_contratos || venda.numeroContratos || 0) : 0;
    return parseInt(numeroContratos, 10);
  };

  // Calculate total agenciamentos for a year
  const calcularAgenciamentosTotal = (ano) => {
    return corretores
      .filter(c => c.ativo)
      .reduce((total, corretor) => {
        if (corretor.vendas && corretor.vendas[ano]) {
          const agenciamentosAno = Object.values(corretor.vendas[ano]).reduce((sum, v) => {
            const agenciamento = typeof v === 'object' ? (v.agenciamento || 0) : 0;
            return sum + parseInt(agenciamento, 10);
          }, 0);
          return total + agenciamentosAno;
        }
        return total;
      }, 0);
  };

  // Calculate total contratos for a year
  const calcularContratosTotal = (ano) => {
    return corretores
      .filter(c => c.ativo)
      .reduce((total, corretor) => {
        if (corretor.vendas && corretor.vendas[ano]) {
          const contratosAno = Object.values(corretor.vendas[ano]).reduce((sum, v) => {
            const numeroContratos = typeof v === 'object' ? (v.numero_contratos || v.numeroContratos || 0) : 0;
            return sum + parseInt(numeroContratos, 10);
          }, 0);
          return total + contratosAno;
        }
        return total;
      }, 0);
  };

  // Calculate agenciamentos by month for a year
  const calcularAgenciamentosMensais = (ano) => {
    return meses.map((mes, index) => {
      const total = corretores
        .filter(c => c.ativo)
        .reduce((sum, corretor) => {
          return sum + getAgenciamento(corretor, ano, mes);
        }, 0);
      return {
        mes: mesesAbrev[index],
        mesCompleto: mes,
        valor: total,
      };
    });
  };

  // Calculate contratos by month for a year
  const calcularContratosMensais = (ano) => {
    return meses.map((mes, index) => {
      const total = corretores
        .filter(c => c.ativo)
        .reduce((sum, corretor) => {
          return sum + getNumeroContratos(corretor, ano, mes);
        }, 0);
      return {
        mes: mesesAbrev[index],
        mesCompleto: mes,
        valor: total,
      };
    });
  };

  // Calculate VGV by month for multiple years
  const calcularVGVMensais = (anos) => {
    return meses.map((mes, index) => {
      const dados = { mes: mesesAbrev[index] };
      anos.forEach(ano => {
        const total = calcularVGVMes(ano, mes);
        dados[`VGV ${ano}`] = total;
      });
      return dados;
    });
  };

  // Calculate stats - recalculate when anoSelecionado or corretores change
  const vgvTotal = useMemo(() => {
    return calcularVGVTotal(anoSelecionado);
  }, [anoSelecionado, corretores]);
  
  const metaAnual = useMemo(() => {
    return getMetaPorAno(anoSelecionado);
  }, [anoSelecionado]);
  
  const percentualMeta = useMemo(() => {
    return metaAnual > 0 ? (vgvTotal / metaAnual) * 100 : 0;
  }, [vgvTotal, metaAnual]);
  
  const totalAgenciamentos = useMemo(() => {
    return calcularAgenciamentosTotal(anoSelecionado);
  }, [anoSelecionado, corretores]);
  
  const totalContratos = useMemo(() => {
    return calcularContratosTotal(anoSelecionado);
  }, [anoSelecionado, corretores]);

  // Prepare chart data - recalculate when anoSelecionado or corretores change
  const dadosAgenciamentosMensais = useMemo(() => {
    return calcularAgenciamentosMensais(anoSelecionado);
  }, [anoSelecionado, corretores]);
  
  const dadosContratosMensais = useMemo(() => {
    return calcularContratosMensais(anoSelecionado);
  }, [anoSelecionado, corretores]);
  
  const dadosVGVMensais = useMemo(() => {
    return calcularVGVMensais(anosSelecionados);
  }, [anosSelecionados, corretores, calcularVGVMes]);

  // Prepare pie chart data for agenciamentos
  const dadosPizzaAgenciamentos = useMemo(() => {
    return dadosAgenciamentosMensais
      .map(item => ({
        name: item.mes,
        value: item.valor,
      }))
      .filter(item => item.value > 0);
  }, [dadosAgenciamentosMensais]);

  const handleMenuClick = (menu) => {
    if (menu === 'Início') {
      return;
    }
    if (menu === 'Dashboard') {
      navigate('/dashboard');
      return;
    }
    const isAuthenticated = localStorage.getItem('adminAuthenticated') === 'true';
    if (!isAuthenticated) {
      navigate('/');
      return;
    }
    if (menu === 'Vendas') {
      navigate('/vendas');
      return;
    }
    if (menu === 'Lista de Corretores') {
      navigate('/corretores');
      return;
    }
    if (menu === 'Relatório') {
      navigate('/relatorio');
      return;
    }
    if (menu === 'Comparativo') {
      navigate('/comparativo');
      return;
    }
    if (menu === 'Apresentação') {
      navigate('/apresentacao');
      return;
    }
    if (menu === 'Configurações') {
      navigate('/configuracoes');
      return;
    }
    navigate('/corretores');
  };

  const formatarMoeda = (valor) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(valor);
  };

  const handleAnoChange = (ano) => {
    setAnosSelecionados(prev => {
      const novosAnos = prev.includes(ano)
        ? prev.filter(a => a !== ano)
        : [...prev, ano].sort();
      
      // Update anoSelecionado to the most recent selected year
      // This ensures all data (cards, charts) reflect the selected year
      if (novosAnos.length > 0) {
        const anoMaisRecente = Math.max(...novosAnos);
        setAnoSelecionado(anoMaisRecente);
      } else {
        // If no year is selected, keep the current year
        setAnoSelecionado(anoAtual);
      }
      
      return novosAnos;
    });
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <Sidebar 
        onSelectMenu={handleMenuClick}
        menuSelecionado={menuSelecionado}
      />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minHeight: '100vh',
          background: `linear-gradient(135deg, ${colors.background.default} 0%, ${colors.background.paper} 100%)`,
          py: 4,
        }}
      >
        <Container maxWidth="xl">
          {/* Header */}
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 'bold',
                  color: colors.text.primary,
                }}
              >
                Visão Geral {anoSelecionado}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={anosSelecionados.includes(anoAnterior)}
                      onChange={() => handleAnoChange(anoAnterior)}
                      sx={{ color: colors.primary }}
                    />
                  }
                  label={anoAnterior}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={anosSelecionados.includes(anoAtual)}
                      onChange={() => handleAnoChange(anoAtual)}
                      sx={{ color: colors.primary }}
                    />
                  }
                  label={anoAtual}
                />
              </Box>
            </Box>
            <Typography
              variant="body1"
              sx={{
                color: 'text.secondary',
              }}
            >
              Resumo executivo de vendas e desempenho
            </Typography>
          </Box>

          {/* Resumo de Performance */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <Paper
                sx={{
                  p: 2,
                  borderRadius: 2,
                  background: `linear-gradient(135deg, ${colors.background.paper} 0%, ${colors.background.default} 100%)`,
                  border: `1px solid ${colors.border?.primary || 'rgba(255, 255, 255, 0.1)'}`,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600, fontSize: '0.75rem' }}>
                    Percentual da Meta
                  </Typography>
                  <ShowChartIcon sx={{ color: '#FF9800', fontSize: 22 }} />
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 'bold',
                    color: '#FF9800',
                    mb: 0.5,
                  }}
                >
                  {percentualMeta.toFixed(1)}%
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                  Meta anual: {formatarMoeda(metaAnual)}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper
                sx={{
                  p: 2,
                  borderRadius: 2,
                  background: `linear-gradient(135deg, ${colors.background.paper} 0%, ${colors.background.default} 100%)`,
                  border: `1px solid ${colors.border?.primary || 'rgba(255, 255, 255, 0.1)'}`,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600, fontSize: '0.75rem' }}>
                    VGV Total
                  </Typography>
                  <AccountBalanceWalletIcon sx={{ color: colors.primary, fontSize: 22 }} />
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 'bold',
                    color: colors.primary,
                    mb: 0.5,
                  }}
                >
                  {formatarMoeda(vgvTotal)}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                  Valor total de vendas em {anoSelecionado}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper
                sx={{
                  p: 2,
                  borderRadius: 2,
                  background: `linear-gradient(135deg, ${colors.background.paper} 0%, ${colors.background.default} 100%)`,
                  border: `1px solid ${colors.border?.primary || 'rgba(255, 255, 255, 0.1)'}`,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600, fontSize: '0.75rem' }}>
                    Meta Anual
                  </Typography>
                  <SettingsIcon sx={{ color: colors.primary, fontSize: 22 }} />
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 'bold',
                    color: colors.text.primary,
                    mb: 0.5,
                  }}
                >
                  {formatarMoeda(metaAnual)}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                  Meta estabelecida para {anoSelecionado}
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* Charts Section */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {/* Total de Agenciamentos */}
            <Grid item xs={12} md={6}>
              <Paper
                sx={{
                  p: 2,
                  borderRadius: 2,
                  background: `linear-gradient(135deg, ${colors.background.paper} 0%, ${colors.background.default} 100%)`,
                  border: `1px solid ${colors.border?.primary || 'rgba(255, 255, 255, 0.1)'}`,
                  height: '100%',
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1.5, fontSize: '1rem' }}>
                  Total de Agenciamentos {anoSelecionado}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: colors.primary, mr: 2 }}>
                    {totalAgenciamentos.toLocaleString('pt-BR')}
                  </Typography>
                </Box>
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie
                      data={dadosPizzaAgenciamentos}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      outerRadius={65}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {dadosPizzaAgenciamentos.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>

            {/* Total de Contratos */}
            <Grid item xs={12} md={6}>
              <Paper
                sx={{
                  p: 2,
                  borderRadius: 2,
                  background: `linear-gradient(135deg, ${colors.background.paper} 0%, ${colors.background.default} 100%)`,
                  border: `1px solid ${colors.border?.primary || 'rgba(255, 255, 255, 0.1)'}`,
                  height: '100%',
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1.5, fontSize: '1rem' }}>
                  Total de Contratos {anoSelecionado}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: colors.primary, mr: 2 }}>
                    {totalContratos.toLocaleString('pt-BR')}
                  </Typography>
                </Box>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={dadosContratosMensais}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="mes" />
                    <YAxis />
                    <Tooltip 
                      formatter={(value) => [`${value} contrato(s)`, 'Contratos']}
                      labelFormatter={(label) => {
                        // Find the full month name from dadosContratosMensais
                        const item = dadosContratosMensais.find(d => d.mes === label);
                        return item ? item.mesCompleto : label;
                      }}
                      contentStyle={{
                        backgroundColor: colors.background.paper,
                        border: `1px solid ${colors.border?.primary || 'rgba(255, 255, 255, 0.1)'}`,
                        borderRadius: '8px',
                        color: colors.text.primary,
                      }}
                    />
                    <Bar dataKey="valor" fill={colors.primary} />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
          </Grid>

          {/* VGV Mensal */}
          <Paper
            sx={{
              p: 2,
              borderRadius: 2,
              background: `linear-gradient(135deg, ${colors.background.paper} 0%, ${colors.background.default} 100%)`,
              border: `1px solid ${colors.border?.primary || 'rgba(255, 255, 255, 0.1)'}`,
              mb: 3,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, fontSize: '1rem' }}>
              VGV Mensal
            </Typography>
            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={dadosVGVMensais}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="mes" />
                <YAxis />
                <Tooltip formatter={(value) => formatarMoeda(value)} />
                <Legend />
                {anosSelecionados.includes(anoAnterior) && (
                  <Line
                    type="monotone"
                    dataKey={`VGV ${anoAnterior}`}
                    stroke="#2196F3"
                    strokeWidth={2}
                    dot={{ r: 4 }}
                  />
                )}
                {anosSelecionados.includes(anoAtual) && (
                  <Line
                    type="monotone"
                    dataKey={`VGV ${anoAtual}`}
                    stroke="#FF9800"
                    strokeWidth={2}
                    dot={{ r: 4 }}
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          </Paper>

          {/* Footer */}
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Typography variant="body2" color="text.secondary">
              Sistema desenvolvido para Prime Class Imobiliária
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}

export default Home;
